import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DocumentArrowDownIcon, FunnelIcon, PencilSquareIcon, TrashIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { utils as XLSXUtils, write as XLSXWrite } from 'xlsx'
import JsPDF from 'jspdf'
import 'jspdf-autotable'
import { useAuth } from 'auth/AuthContext'
import { parseDate, calculateNetHours } from 'utils'
import { showAlert } from 'services/alerts.service'
import { makeRequest } from 'services/request.service'
import EditWorkdayModal from 'components/modals/EditWorkdayModal'
import ModalComponent from 'components/common/ModalComponent'
import ButtonComponent from 'components/common/ButtonComponent'
import ActionCardComponent from 'components/common/ActionCardComponent'
import TableComponent from 'components/common/TableComponent'
import PageLoadingState from 'components/common/PageLoadingState'
import DateRangeFilterModal from 'components/modals/DateRangeFilterModal'
import { DateTime } from 'luxon'
import { useDashboardDateRange } from 'hooks/useDashboardDateRange'
import { useDashboardFilterOptions } from 'hooks/useDashboardFilterOptions'
import { buildDashboardDateRangeQuery, getDateRangeSummary } from 'utils/dateRangeFilters'
import { filterRowsBySearch } from 'utils/dataEditExport'

const Workdays = () => {
  const { user } = useAuth()
  const { t } = useTranslation()
  const currentDate = DateTime.now().setZone('America/Mexico_City').toFormat('yyyy-MM-dd')
  const [dateRange, setDateRange] = useDashboardDateRange(currentDate)
  const filterOptions = useDashboardFilterOptions(user?.farmId)
  const [showFilters, setShowFilters] = useState(false)

  const [state, setState] = useState({
    workdays: [],
    selectedWorkDay: null,
    showEditModal: false,
    showDeleteConfirm: false,
    pendingDelete: null,
    loading: true,
    searchTerm: ''
  })

  const handleExportExcel = () => {
    try {
      const dataToExport = filteredWorkdays.map(worker => ({
        Number: worker.oldId || worker.workerId,
        'Worker Name': worker.workerName,
        Date: worker.date,
        'Start Time': worker.startTime,
        'End Time': worker.endTime,
        'Net Hours': worker.netHours,
        Edited: worker.edited ? 'Yes' : 'No'
      }))

      const worksheet = XLSXUtils.json_to_sheet(dataToExport)
      const workbook = XLSXUtils.book_new()
      XLSXUtils.book_append_sheet(workbook, worksheet, 'Workday')

      const excelBuffer = XLSXWrite(workbook, {
        bookType: 'xlsx',
        type: 'array'
      })

      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `workday_${new Date().toISOString().split('T')[0]}.xlsx`

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      window.URL.revokeObjectURL(url)

      showAlert({
        text: t('mgmt.common.excelDownloaded'),
        icon: 'success'
      })
    } catch (error) {
      console.error('Error exporting to Excel:', error)
      showAlert({
        text: t('mgmt.common.excelFailed'),
        icon: 'error'
      })
    }
  }

  const handleExportPDF = () => {
    try {
      const doc = new JsPDF()

      doc.setFontSize(16)
      doc.text(t('mgmt.workdays.reportTitle'), 14, 15)
      doc.setFontSize(10)
      doc.text(t('mgmt.common.generatedOn', { date: new Date().toLocaleDateString() }), 14, 25)

      const tableColumn = [
        'Number',
        'Worker Name',
        'Date',
        'Start Time',
        'End Time',
        'Net Hours',
        'Edited'
      ]

      const tableRows = filteredWorkdays.map(workDay => [
        workDay.oldId || workDay.workerId,
        workDay.workerName,
        workDay.date,
        workDay.startTime,
        workDay.endTime,
        workDay.netHours,
        workDay.edited ? 'Yes' : 'No'
      ])

      // @ts-ignore
      doc.autoTable({
        startY: 30,
        head: [tableColumn],
        body: tableRows,
        theme: 'grid',
        styles: {
          fontSize: 8,
          cellPadding: 2
        },
        headStyles: {
          fillColor: [67, 160, 71],
          textColor: 255
        }
      })

      doc.save(`workday_${new Date().toISOString().split('T')[0]}.pdf`)

      showAlert({
        text: t('mgmt.common.pdfDownloaded'),
        icon: 'success'
      })
    } catch (error) {
      console.error('Error exporting to PDF:', error)
      showAlert({
        text: t('mgmt.common.pdfFailed'),
        icon: 'error'
      })
    }
  }

  const handleEditWorkday = async (workday) => {
    try {
      const response = await makeRequest({
        method: 'patch',
        url: `/management/workdays/${workday.workdayId}`,
        data: {
          endTime: workday.endTime,
          startTime: workday.startTime
        },
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` },
        alertResult: true
      })
      if (response.status === 200) {
        setState(s => ({
          ...s,
          showEditModal: false,
          selectedWorkDay: null
        }))
        await fetchWorkdays()
      }
    } catch (error) {
      showAlert({
        text: t('mgmt.workdays.errUpdate'),
        icon: 'error'
      })
      console.error('Error updating workday:', error)
    }
  }

  const handleDelete = (workday) => {
    setState(s => ({ ...s, showDeleteConfirm: true, pendingDelete: workday }))
  }

  const confirmDelete = async () => {
    const workday = state.pendingDelete
    setState(s => ({ ...s, showDeleteConfirm: false, pendingDelete: null }))
    try {
      await makeRequest({
        method: 'DELETE',
        url: `/management/workdays/${workday.workdayId}`,
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` },
        alertResult: true
      })
    } catch (error) {
      showAlert({ text: t('mgmt.workdays.errDelete'), icon: 'error' })
      console.error('Error deleting workday:', error)
    } finally {
      fetchWorkdays()
    }
  }

  const handleEditModal = (workday) => {
    setState(prev => ({
      ...prev,
      selectedWorkDay: workday,
      showEditModal: true
    }))
  }

  const handleCloseModal = () => {
    setState(s => ({
      ...s,
      showEditModal: false,
      selectedWorkDay: null
    }))
  }

  const getUrl = () => {
    const baseUrl = `/management/workdays/${user.farmId}`
    return `${baseUrl}?${buildDashboardDateRangeQuery(dateRange)}`
  }

  const fetchWorkdays = async () => {
    try {
      // @ts-ignore
      const response = await makeRequest({
        url: getUrl(),
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
      })
      if (response.status === 200) {
        const workdaysWithNetHours = response.data.map(workday => ({
          ...workday,
          date: parseDate(workday.createdAt, false),
          netHours: calculateNetHours(workday.startTime, workday.endTime)
        }))
        setState(s => ({ ...s, workdays: workdaysWithNetHours, loading: false }))
      }
    } catch (error) {
      console.error('Error fetching workdays:', error)
      setState(s => ({ ...s, loading: false }))
    }
  }

  const filteredWorkdays = filterRowsBySearch(state.workdays, state.searchTerm)

  const workdayTableData = {
    columns: [
      // Workers are still identified by their legacy id (old_id); fall back to
      // the internal worker_id only when a worker has no legacy id.
      { key: 'workerId', labelKey: 'mgmt.workdays.cols.workerNumber', cell: (value, row) => row.oldId || row.workerId },
      { key: 'workerName', labelKey: 'mgmt.workdays.cols.workerName' },
      { key: 'date', labelKey: 'mgmt.workdays.cols.date' },
      { key: 'startTime', labelKey: 'mgmt.workdays.cols.startTime' },
      { key: 'endTime', labelKey: 'mgmt.workdays.cols.endTime' },
      { key: 'netHours', labelKey: 'mgmt.workdays.cols.netHours' },
      {
        key: 'edited',
        labelKey: 'mgmt.workdays.cols.edited',
        cell: (value) => value ? t('mgmt.common.yes') : t('mgmt.common.no')
      },
      {
        key: 'actions',
        labelKey: 'mgmt.workdays.cols.manage',
        // @ts-ignore
        cell: (value, row) => {
          return (
            <div className='flex items-center gap-2'>
              <button onClick={() => handleEditModal(row)} title={t('common.edit')} className='p-1 rounded transition'>
                <PencilSquareIcon className='w-5 h-5 text-blue-500' />
              </button>
              <button onClick={() => handleDelete(row)} title={t('common.delete')} className='p-1 rounded transition'>
                <TrashIcon className='w-5 h-5 text-red-500' />
              </button>
            </div>
          )
        }
      }
    ],
    rows: filteredWorkdays
  }

  useEffect(() => {
    if (!user?.farmId) return
    fetchWorkdays()
  }, [user?.farmId, dateRange])

  if (state.loading) {
    return <PageLoadingState message={t('mgmt.workdays.loading')} rows={8} />
  }

  return (
    <>
      <ActionCardComponent>
        <input
          type='text'
          placeholder={t('mgmt.workdays.search')}
          value={state.searchTerm}
          name='filter'
          onChange={(e) => setState(s => ({ ...s, searchTerm: e.target.value.toLowerCase() }))}
          className='search-input'
          data-align='left'
        />
        <span className='text-sm text-gray-700'>{getDateRangeSummary(dateRange)}</span>
        <ButtonComponent onClick={() => setShowFilters(true)}>
          <FunnelIcon className='w-5 h-5' />
          <span>{t('dashboard.advancedFilters')}</span>
        </ButtonComponent>
        <ButtonComponent onClick={handleExportExcel}>
          <DocumentArrowDownIcon className='w-5 h-5' />
          <span>{t('common.exportExcel')}</span>
        </ButtonComponent>
        <ButtonComponent onClick={handleExportPDF}>
          <DocumentArrowDownIcon className='w-5 h-5' />
          <span>{t('mgmt.common.exportPdf')}</span>
        </ButtonComponent>
      </ActionCardComponent>

      <TableComponent
        tableData={workdayTableData}
        handleEdit={handleEditModal}
        defaultSortColumn={workdayTableData.columns[0]?.key}
      />

      <ModalComponent
        isOpen={state.showDeleteConfirm}
        onClose={() => setState(s => ({ ...s, showDeleteConfirm: false, pendingDelete: null }))}
        title={t('mgmt.workdays.deleteTitle')}
        maxWidth='sm'
        hideDefaultFooter
      >
        <div className='flex flex-col items-center gap-4 py-2'>
          <ExclamationTriangleIcon className='w-12 h-12 text-red-400' />
          <p className='text-sm text-gray-700 text-center'>
            {state.pendingDelete?.date
              ? t('mgmt.workdays.removeConfirmWithDate', { name: state.pendingDelete?.workerName ?? '', date: state.pendingDelete.date })
              : t('mgmt.workdays.removeConfirm', { name: state.pendingDelete?.workerName ?? '' })}
            <br />
            <span className='text-gray-500'>{t('mgmt.common.confirmDelete')}</span>
          </p>
          <div className='flex justify-end gap-3 w-full mt-2'>
            <button
              type='button'
              onClick={() => setState(s => ({ ...s, showDeleteConfirm: false, pendingDelete: null }))}
              className='px-4 py-2 rounded bg-gray-100 text-greenPrimary hover:bg-gray-200 transition'
            >
              {t('mgmt.common.cancel')}
            </button>
            <button
              type='button'
              onClick={confirmDelete}
              className='px-4 py-2 rounded bg-red-500 text-white hover:bg-red-600 transition'
            >
              {t('mgmt.common.delete')}
            </button>
          </div>
        </div>
      </ModalComponent>

      <EditWorkdayModal
        isOpen={state.showEditModal}
        workday={state.selectedWorkDay}
        onClose={handleCloseModal}
        onSave={handleEditWorkday}
      />
      <DateRangeFilterModal
        isOpen={showFilters}
        dateRange={dateRange}
        sectors={filterOptions.sectors}
        farmingBlocks={filterOptions.farmingBlocks}
        groups={filterOptions.groups}
        activities={filterOptions.activities}
        maxDate={currentDate}
        onClose={() => setShowFilters(false)}
        onApply={(nextRange) => {
          setDateRange(nextRange)
          setShowFilters(false)
        }}
      />
    </>
  )
}

export default Workdays
